// Træningsbane — ét emne (~250–300 tegn) ad gangen, pause mellem emner.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  KeyboardLayoutId,
  RoundResult,
  Trin,
  UserProfile,
} from '../../types';
import type { PreparedRound } from '../../lib/lessonGenerator';
import {
  type FlowChunkPick,
  pickFlowChunkStatic,
} from '../../lib/flowChunks';
import { FLOW_CHUNKS_DA } from '../../data/flowPractice/chunks-da';
import { FLOW_CHUNKS_EN } from '../../data/flowPractice/chunks-en';
import { fetchFlowChunk } from '../../lib/flowApi';
import {
  effectiveFlowLexicon,
  flowTopicLabel,
  mergeFlowSegmentResults,
} from '../../lib/flowPractice';
import { wpm as wpmFn } from '../../lib/stats';
import { useRoundInput } from '../../lib/useRoundInput';
import ParagraphTextDisplay from '../ParagraphTextDisplay';
import Keyboard, { fingerNameForKey } from '../Keyboard';

interface FlowRoundProps {
  round: PreparedRound;
  trin: Trin;
  keyboardLayout: KeyboardLayoutId;
  user: UserProfile;
  onFinish: (r: RoundResult) => void;
}

type Phase = 'typing' | 'pause';

function findInitialPassage(
  lexicon: ReturnType<typeof effectiveFlowLexicon>,
  targetText: string,
): FlowChunkPick {
  const pool = lexicon === 'en' ? FLOW_CHUNKS_EN : FLOW_CHUNKS_DA;
  const match = pool.find((c) => c.text === targetText);
  if (match) return match;
  return { text: targetText, topic: pickFlowChunkStatic(lexicon, []).topic };
}

export default function FlowRound({
  round,
  trin,
  keyboardLayout,
  user,
  onFinish,
}: FlowRoundProps) {
  const lexicon = effectiveFlowLexicon(user);
  const [passage, setPassage] = useState<FlowChunkPick>(() =>
    findInitialPassage(lexicon, round.targetText),
  );
  const [phase, setPhase] = useState<Phase>('typing');
  const [passageKey, setPassageKey] = useState(0);
  const [frozenSegment, setFrozenSegment] = useState<RoundResult | null>(null);

  const segmentsRef = useRef<RoundResult[]>([]);
  const usedTopicsRef = useRef<string[]>([]);
  const nextPassageRef = useRef<FlowChunkPick | null>(null);
  const fetchingRef = useRef(false);

  const prefetchPassage = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const remote = await fetchFlowChunk({
        lexicon,
        excludeTopics: usedTopicsRef.current,
      });
      if (remote?.text) {
        nextPassageRef.current = { text: remote.text, topic: remote.topic };
        return;
      }
      nextPassageRef.current = pickFlowChunkStatic(
        lexicon,
        usedTopicsRef.current,
        Date.now(),
      );
    } finally {
      fetchingRef.current = false;
    }
  }, [lexicon]);

  useEffect(() => {
    void prefetchPassage();
  }, [prefetchPassage]);

  const endSession = useCallback(
    (segments: RoundResult[]) => {
      if (segments.length === 0) return;
      onFinish(
        mergeFlowSegmentResults(segments, {
          roundId: round.template.id,
          title: round.template.title,
          mode: round.template.mode,
          flowLexicon: lexicon,
        }),
      );
    },
    [onFinish, round.template, lexicon],
  );

  const handleSegmentDone = useCallback(
    (result: RoundResult) => {
      if (!result.completed) {
        endSession([...segmentsRef.current, result]);
        return;
      }
      segmentsRef.current = [...segmentsRef.current, result];
      usedTopicsRef.current = [
        ...usedTopicsRef.current,
        passage.topic,
      ].slice(-24);
      setFrozenSegment(result);
      setPhase('pause');
      void prefetchPassage();
    },
    [endSession, passage.topic, prefetchPassage],
  );

  const handleNextTopic = useCallback(() => {
    const next =
      nextPassageRef.current ??
      pickFlowChunkStatic(lexicon, usedTopicsRef.current, Date.now());
    nextPassageRef.current = null;
    setPassage(next);
    setPassageKey((k) => k + 1);
    setFrozenSegment(null);
    setPhase('typing');
    void prefetchPassage();
  }, [lexicon, prefetchPassage]);

  const handleDone = useCallback(() => {
    endSession(segmentsRef.current);
  }, [endSession]);

  const sessionSummary = useMemo(() => {
    const segments = segmentsRef.current;
    const durationMs = segments.reduce((a, r) => a + r.durationMs, 0);
    const correctChars = segments.reduce((a, r) => a + r.correctChars, 0);
    return {
      count: segments.length,
      durationMs,
      wpm: wpmFn(correctChars, durationMs),
    };
  }, [frozenSegment, phase]);

  if (phase === 'pause' && frozenSegment) {
    return (
      <FlowPauseView
        topic={passage.topic}
        segment={frozenSegment}
        sessionCount={sessionSummary.count}
        sessionWpm={sessionSummary.wpm}
        onNext={handleNextTopic}
        onDone={handleDone}
      />
    );
  }

  return (
    <FlowPassageTyping
      key={passageKey}
      passage={passage}
      round={round}
      trin={trin}
      keyboardLayout={keyboardLayout}
      onSegmentDone={handleSegmentDone}
      onDone={handleDone}
      completedCount={segmentsRef.current.length}
    />
  );
}

interface FlowPassageTypingProps {
  passage: FlowChunkPick;
  round: PreparedRound;
  trin: Trin;
  keyboardLayout: KeyboardLayoutId;
  onSegmentDone: (r: RoundResult) => void;
  onDone: () => void;
  completedCount: number;
}

function FlowPassageTyping({
  passage,
  round,
  trin,
  keyboardLayout,
  onSegmentDone,
  onDone,
  completedCount,
}: FlowPassageTypingProps) {
  const finishRef = useRef<(completed: boolean) => void>(() => {});

  const input = useRoundInput({
    targetText: passage.text,
    mode: round.template.mode,
    roundId: round.template.id,
    roundTitle: round.template.title,
    presentationKind: 'flow',
    strictCase: true,
    keyboardLayoutId: keyboardLayout,
    onFinish: onSegmentDone,
    deferFinishOnComplete: true,
    onAdvance: ({ completed }) => {
      if (completed) finishRef.current(true);
    },
  });

  finishRef.current = input.finish;

  return (
    <>
      <p className="label">
        Emne: {flowTopicLabel(passage.topic)}
        {completedCount > 0 && (
          <span className="text-faint">
            {' '}
            · {completedCount}{' '}
            {completedCount === 1 ? 'tekst' : 'tekster'} skrevet
          </span>
        )}
      </p>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Tid" value={formatMs(input.elapsedMs)} />
        <Stat label="WPM" value={input.liveWpm.toFixed(0)} />
        <Stat
          label="Nøjagtighed"
          value={`${(input.liveAcc * 100).toFixed(0)}%`}
        />
        <Stat
          label="Tegn"
          value={`${input.cursor} / ${passage.text.length}`}
        />
      </div>

      <ParagraphTextDisplay
        text={passage.text}
        cursor={input.cursor}
        correctMask={input.correctMask}
      />

      <div className="space-y-2">
        <Keyboard
          layoutId={keyboardLayout}
          nextExpectedKey={input.expectedChar}
          lastPressedKey={input.lastPress?.key}
          lastPressCorrect={input.lastPress?.correct}
          unlockedKeys={trin.unlockedKeys}
          hintLevel={input.hintLevel}
          showFingerDot={input.hintLevel !== 'none'}
        />
        <div className="flex h-6 items-center justify-center text-xs">
          {input.strongHintActive && input.expectedChar && (
            <span className="text-muted animate-fade-in-up">
              Brug{' '}
              {fingerNameForKey(input.expectedChar, keyboardLayout)?.toLowerCase()}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center">
        <button
          type="button"
          onClick={onDone}
          className="rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-400"
        >
          Jeg er færdig
        </button>
        <p className="text-center text-xs text-faint">
          Escape stopper sessionen
        </p>
      </div>
    </>
  );
}

interface FlowPauseViewProps {
  topic: string;
  segment: RoundResult;
  sessionCount: number;
  sessionWpm: number;
  onNext: () => void;
  onDone: () => void;
}

function FlowPauseView({
  topic,
  segment,
  sessionCount,
  sessionWpm,
  onNext,
  onDone,
}: FlowPauseViewProps) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== ' ' || e.repeat) return;
      const el = e.target;
      if (
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        el instanceof HTMLSelectElement
      ) {
        return;
      }
      e.preventDefault();
      onNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onNext]);

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col items-center gap-6 text-center animate-fade-in-up">
      <div>
        <p className="label">Pause</p>
        <h2 className="mt-2 text-2xl font-semibold text-strong">
          {flowTopicLabel(topic)}
        </h2>
        <p className="mt-2 text-sm text-muted">
          Teksten er skrevet. Tag en kort pause — eller fortsæt til næste emne.
        </p>
      </div>

      <div className="grid w-full grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="WPM" value={segment.wpm.toFixed(0)} />
        <Stat
          label="Nøjagtighed"
          value={`${(segment.accuracy * 100).toFixed(0)}%`}
        />
        <Stat label="Tid" value={formatMs(segment.durationMs)} />
        <Stat label="Session WPM" value={sessionWpm.toFixed(0)} />
      </div>

      <p className="text-xs text-faint">
        {sessionCount} {sessionCount === 1 ? 'tekst' : 'tekster'} i denne
        session
      </p>

      <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
        <button
          type="button"
          autoFocus
          onClick={onNext}
          className="rounded-xl bg-indigo-500 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-indigo-400 dark:shadow-lg dark:shadow-indigo-500/30"
        >
          Næste emne
        </button>
        <button type="button" onClick={onDone} className="btn-secondary">
          Jeg er færdig
        </button>
      </div>
      <p className="text-xs text-faint">Tryk Space for næste emne</p>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="surface-soft px-4 py-3">
      <p className="label">{label}</p>
      <p className="mt-1 font-mono text-2xl text-strong">{value}</p>
    </div>
  );
}

function formatMs(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
}
